import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const SitePolicyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('introduction');

  useEffect(() => {
    // Smooth scroll behavior for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(this: HTMLAnchorElement, e: MouseEvent) {
        e.preventDefault();
        const targetId = this.getAttribute('href')?.substring(1);
        if (!targetId) return;
        
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop - 100, // Offset to account for sticky header
            behavior: 'smooth'
          });
          
          // Update URL hash without jumping
          window.history.pushState(null, '', `#${targetId}`);
          
          // Update active section
          setActiveSection(targetId);
        }
      });
    });

    // Update active section based on scroll position
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Add offset for better UX

      // Get all section elements
      const sections = document.querySelectorAll('section[id]');

      // Find the current section
      sections.forEach(section => {
        const sectionTop = (section as HTMLElement).offsetTop;
        const sectionHeight = section.clientHeight;
        const sectionId = section.getAttribute('id') || '';

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initialize on mount

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{
      background: 'linear-gradient(135deg, #141019 0%, #1A1625 50%, #1F1A27 100%)',
    }}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/10 blur-[170px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-500/10 blur-[170px] rounded-full"></div>

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(to right, #ffffff 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Small decorative dots */}
      <div className="absolute top-20 left-10 w-24 h-24 opacity-20">
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-0 left-0"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-0 left-8"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-0 left-16"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-8 left-0"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-8 left-8"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-8 left-16"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-16 left-0"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-16 left-8"></div>
        <div className="w-2 h-2 bg-[#F670C7] rounded-full absolute top-16 left-16"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block mb-4 px-4 py-1 bg-white/10 backdrop-blur-md rounded-full text-[#F670C7] text-sm font-medium"
            >
              Legal Information
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight"
            >
              Site Policy
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/70 text-lg max-w-2xl mx-auto"
            >
              Our guidelines and rules for using the Developer Marketplace platform.
            </motion.p>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="prose prose-invert prose-headings:font-medium prose-headings:mb-4 prose-p:mb-4 prose-p:text-white/80 prose-li:text-white/80 prose-li:mb-2 max-w-none backdrop-blur-sm bg-gradient-to-r from-[#1F1A27]/90 to-[#2A1E44]/90 p-8 md:p-10 rounded-2xl shadow-xl border border-white/10"
          >
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
              <p className="text-white text-lg m-0">
                Last updated: June 15, 2023
              </p>

              <div className="flex items-center space-x-3">
                <button onClick={() => window.print()} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Print</span>
                </button>

                <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Copy Link</span>
                </button>
              </div>
            </div>

            <div className="md:grid md:grid-cols-[250px_1fr] gap-8">
              {/* Table of Contents (Desktop) */}
              <div className="hidden md:block">
                <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pr-4 pb-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                  <h4 className="text-sm uppercase tracking-wider text-white/50 mb-4 font-medium">Contents</h4>
                  <nav className="space-y-2">
                    <a href="#introduction" className={`block transition-colors ${activeSection === 'introduction' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Introduction</a>
                    <a href="#content-guidelines" className={`block transition-colors ${activeSection === 'content-guidelines' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Content Guidelines</a>
                    <a href="#prohibited-content" className={`block transition-colors ${activeSection === 'prohibited-content' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Prohibited Content</a>
                    <a href="#seller-responsibilities" className={`block transition-colors ${activeSection === 'seller-responsibilities' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Seller Responsibilities</a>
                    <a href="#buyer-responsibilities" className={`block transition-colors ${activeSection === 'buyer-responsibilities' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Buyer Responsibilities</a>
                    <a href="#reviews-ratings" className={`block transition-colors ${activeSection === 'reviews-ratings' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Reviews and Ratings</a>
                    <a href="#dispute-resolution" className={`block transition-colors ${activeSection === 'dispute-resolution' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Dispute Resolution</a>
                    <a href="#intellectual-property" className={`block transition-colors ${activeSection === 'intellectual-property' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Intellectual Property</a>
                    <a href="#platform-security" className={`block transition-colors ${activeSection === 'platform-security' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Platform Security</a>
                    <a href="#changes" className={`block transition-colors ${activeSection === 'changes' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Changes to Site Policy</a>
                    <a href="#related-policies" className={`block transition-colors ${activeSection === 'related-policies' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Related Policies</a>
                    <a href="#contact" className={`block transition-colors ${activeSection === 'contact' ? 'text-[#F670C7] font-medium' : 'text-white/70 hover:text-[#F670C7]'}`}>Contact Us</a>
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div>
                <section id="introduction">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Introduction</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Welcome to Developer Marketplace. These Site Policies outline the rules and guidelines for using our platform. Our mission is to create a safe, respectful, and productive environment for developers to buy and sell digital assets.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    By accessing or using our platform, you agree to comply with these policies. Violation of these policies may result in actions against your account, including temporary or permanent suspension.
                  </p>
                </section>

                <section id="content-guidelines">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Content Guidelines</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    All content uploaded to Developer Marketplace must comply with the following guidelines:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">You must have the legal right to sell or distribute the content.</li>
                    <li className="text-white/90 leading-relaxed">Content must not infringe on intellectual property rights, including copyrights, trademarks, or patents.</li>
                    <li className="text-white/90 leading-relaxed">Content must not contain malicious code, malware, or viruses.</li>
                    <li className="text-white/90 leading-relaxed">Content must be accurately described, including functionality, compatibility, and any dependencies.</li>
                    <li className="text-white/90 leading-relaxed">Content must comply with all applicable laws and regulations.</li>
                  </ul>
                </section>

                <section id="prohibited-content">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Prohibited Content</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    The following types of content are prohibited on Developer Marketplace:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Content that promotes illegal activities or violates applicable laws.</li>
                    <li className="text-white/90 leading-relaxed">Content that contains hate speech, discrimination, or harassment targeting individuals or groups.</li>
                    <li className="text-white/90 leading-relaxed">Content that is sexually explicit or contains adult material.</li>
                    <li className="text-white/90 leading-relaxed">Content that is misleading or deceptive.</li>
                    <li className="text-white/90 leading-relaxed">Content intended for hacking, cracking, or unauthorized access to systems.</li>
                    <li className="text-white/90 leading-relaxed">Content that violates privacy or collects data without proper consent.</li>
                    <li className="text-white/90 leading-relaxed">Content that infringes on others' intellectual property rights.</li>
                  </ul>
                </section>

                <section id="seller-responsibilities">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Seller Responsibilities</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    As a seller on Developer Marketplace, you have the following responsibilities:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Providing accurate descriptions of your products.</li>
                    <li className="text-white/90 leading-relaxed">Responding to buyer inquiries in a timely manner.</li>
                    <li className="text-white/90 leading-relaxed">Providing reasonable support for your products as specified in your listing.</li>
                    <li className="text-white/90 leading-relaxed">Keeping your products up-to-date and addressing critical bugs or security issues.</li>
                    <li className="text-white/90 leading-relaxed">Complying with our refund policy.</li>
                    <li className="text-white/90 leading-relaxed">Ensuring your content doesn't violate any third-party terms or licenses.</li>
                  </ul>
                </section>

                <section id="buyer-responsibilities">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Buyer Responsibilities</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    As a buyer on Developer Marketplace, you have the following responsibilities:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Reading product descriptions carefully before making a purchase.</li>
                    <li className="text-white/90 leading-relaxed">Ensuring the product meets your needs and is compatible with your system.</li>
                    <li className="text-white/90 leading-relaxed">Using products in accordance with their licenses.</li>
                    <li className="text-white/90 leading-relaxed">Not redistributing or reselling products unless explicitly permitted.</li>
                    <li className="text-white/90 leading-relaxed">Providing constructive feedback and reviews.</li>
                  </ul>
                </section>

                <section id="reviews-ratings">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Reviews and Ratings</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    Our review system helps maintain quality on the platform. When leaving reviews:
                  </p>
                  <ul className="list-disc pl-6 mb-4 space-y-2">
                    <li className="text-white/90 leading-relaxed">Be honest and objective in your assessment.</li>
                    <li className="text-white/90 leading-relaxed">Focus on the product itself, not personal issues with the seller.</li>
                    <li className="text-white/90 leading-relaxed">Do not use reviews for harassment or to unfairly target a seller.</li>
                    <li className="text-white/90 leading-relaxed">Be specific about what you liked or disliked about the product.</li>
                    <li className="text-white/90 leading-relaxed">Avoid inappropriate or offensive language.</li>
                  </ul>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We reserve the right to remove reviews that violate these guidelines.
                  </p>
                </section>

                <section id="dispute-resolution">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Dispute Resolution</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    If you experience an issue with a transaction:
                  </p>
                  <ol className="list-decimal pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">First, try to resolve the issue directly with the other party through our messaging system.</li>
                    <li className="text-white/90 leading-relaxed">If that doesn't resolve the issue, contact our support team with details of the problem.</li>
                    <li className="text-white/90 leading-relaxed">Our team will review the situation and help mediate a resolution.</li>
                    <li className="text-white/90 leading-relaxed">In cases where our terms have been violated, we may take direct action.</li>
                  </ol>
                </section>

                <section id="intellectual-property">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Intellectual Property</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    Developer Marketplace respects intellectual property rights and expects all users to do the same. If you believe your intellectual property rights have been violated on our platform:
                  </p>
                  <ol className="list-decimal pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Submit a detailed report through our IP infringement form.</li>
                    <li className="text-white/90 leading-relaxed">Include evidence of your ownership of the IP in question.</li>
                    <li className="text-white/90 leading-relaxed">Specify where on our platform the infringing content appears.</li>
                    <li className="text-white/90 leading-relaxed">We will investigate and take appropriate action, which may include removing the content.</li>
                  </ol>
                </section>

                <section id="platform-security">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Platform Security</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    To maintain the security of our platform:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Do not attempt to breach or test our security measures without explicit permission.</li>
                    <li className="text-white/90 leading-relaxed">Report any security vulnerabilities you discover to our security team.</li>
                    <li className="text-white/90 leading-relaxed">Do not attempt to access other users' accounts or data.</li>
                    <li className="text-white/90 leading-relaxed">Use secure and unique passwords for your account.</li>
                    <li className="text-white/90 leading-relaxed">Enable two-factor authentication when available.</li>
                  </ul>
                </section>

                <section id="changes">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Changes to Site Policy</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We may update our Site Policy from time to time. We will notify users of any significant changes via email or site notifications. Continued use of the platform after policy changes constitutes acceptance of the new terms.
                  </p>
                </section>

                <section id="related-policies">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Related Policies</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    This Site Policy should be read alongside our other policies:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed"><Link to="/terms" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">Terms and Conditions</Link></li>
                    <li className="text-white/90 leading-relaxed"><Link to="/privacy" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">Privacy Policy</Link></li>
                    <li className="text-white/90 leading-relaxed"><Link to="/cookies" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">Cookies Policy</Link></li>
                    <li className="text-white/90 leading-relaxed"><Link to="/refund" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">Refund Policy</Link></li>
                  </ul>
                </section>

                <section id="contact">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Contact Us</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    If you have any questions about our Site Policy, please contact us:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">By email: <span className="text-[#F670C7]">policy@developermarketplace.com</span></li>
                    <li className="text-white/90 leading-relaxed">By visiting the <Link to="/contact" className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">contact page</Link> on our website</li>
                  </ul>
                </section>
              </div>
            </div>

            <div className="mt-16 pt-10 border-t border-white/10">
              <div className="bg-[#2A1E44]/70 p-8 rounded-xl backdrop-blur-sm mt-10 border border-white/10 shadow-lg">
                <div className="flex flex-col md:flex-row items-start md:items-center">
                  <div className="flex-shrink-0 mb-4 md:mb-0 mr-0 md:mr-6">
                    <div className="h-12 w-12 bg-white/10 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-white">Report a Violation</h3>
                    <p className="text-white/90 leading-relaxed mb-5 text-base">
                      If you believe someone is violating our Site Policy, please help us by reporting it.
                    </p>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Link to="/report" className="bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white py-3 px-6 rounded-lg inline-block hover:opacity-90 transition duration-300 font-medium shadow-lg shadow-[#F670C7]/20">
                        Report Violation
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SitePolicyPage;
